/**
 * CR111C: Root Error Boundary for catching unhandled errors in the app
 * Provides a visible fallback UI with error details and reload option
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class RootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[RootErrorBoundary] Caught error:", error);
    console.error("[RootErrorBoundary] Component stack:", errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const { fallbackTitle = "Something went wrong" } = this.props;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-card border border-destructive/50 rounded-lg shadow-lg p-6 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-8 w-8" />
              <h1 className="text-xl font-bold">{fallbackTitle}</h1>
            </div>
            
            {/* CR111C Canary */}
            <div className="text-xs text-muted-foreground font-mono">
              [Canary: CR111C-RootErrorBoundary]
            </div>

            {/* Error Message */}
            <div className="bg-destructive/10 border border-destructive/20 rounded p-4">
              <p className="font-mono text-sm text-destructive break-words">
                {error?.message || "Unknown error"}
              </p>
            </div>

            {/* Stack Trace (Collapsible) */}
            {errorInfo?.componentStack && (
              <details className="bg-muted/50 rounded p-3">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  Show Component Stack
                </summary>
                <pre className="mt-2 text-xs overflow-auto max-h-64 whitespace-pre-wrap text-muted-foreground">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}

            {error?.stack && (
              <details className="bg-muted/50 rounded p-3">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  Show Error Stack
                </summary>
                <pre className="mt-2 text-xs overflow-auto max-h-64 whitespace-pre-wrap text-muted-foreground">
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </button>
              <button
                onClick={this.handleReset}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
              >
                Try Again
              </button>
            </div>

            {/* Help Text */}
            <p className="text-xs text-muted-foreground">
              If this error persists, try refreshing the page or clearing your browser cache.
              Check the browser console for additional details.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;
